import { useQuery } from '@tanstack/react-query'
import { dataSource } from '../../service/domain/_service.config'
import { useChallengeResolution } from '../challenge/useChallengeId'
import { useUser } from '../user/useUser'
import { useTeamMemberships } from './useTeamMemberships'

export const ENTITLEMENT_QUERY_KEY = (userID: string, challengeID: string) =>
  ['entitlement', userID, challengeID] as const

/** Membership + entitlement for a challenge (defaults to active). */
export function useChallengeAccess(challengeId?: string) {
  const resolution = useChallengeResolution()
  const resolvedChallengeId = challengeId || resolution.id
  const { data: user } = useUser()
  const { data: memberships, isLoading: membershipsLoading } = useTeamMemberships()

  const visibleMemberships = resolution.isResolving
    ? (memberships ?? [])
    : (memberships ?? []).filter((membership) => resolution.canSee(membership.challengeID))

  const membership =
    visibleMemberships.find((row) => row.challengeID === resolvedChallengeId) ?? null

  const entitlementQuery = useQuery({
    queryKey: ENTITLEMENT_QUERY_KEY(user?.id ?? '', resolvedChallengeId),
    queryFn: () => dataSource.isUserEntitled(user!.id, resolvedChallengeId),
    enabled: !!user?.id && !!resolvedChallengeId,
  })

  const isEntitled = entitlementQuery.data === true
  const isFreeAgent = isEntitled && !membership
  const teamID = membership?.teamID ?? ''
  const hasAnyMembership = visibleMemberships.length > 0

  return {
    challengeId: resolvedChallengeId,
    membership,
    teamID,
    isEntitled,
    isFreeAgent,
    hasAnyMembership,
    memberships: visibleMemberships,
    isLoading:
      resolution.isResolving ||
      membershipsLoading ||
      (!!user?.id && !!resolvedChallengeId && entitlementQuery.isLoading),
    refetchEntitlement: entitlementQuery.refetch,
  }
}
